// WorkspaceCover.jsx

import React, { useEffect, useState } from 'react';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './WorkspaceCover.css';

function isDirectRenderableImageUrl(url) {
    if (!url || typeof url !== 'string') return false;

    return (
        url.startsWith('blob:') ||
        url.startsWith('data:') ||
        url.startsWith('http://') ||
        url.startsWith('https://') ||
        url.startsWith('/')
    );
}

function getWorkspaceInitials(title) {
    if (!title || typeof title !== 'string') return '?';

    const words = title.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';

    const [firstWord] = words;
    return firstWord.charAt(0).toUpperCase() || '?';
}

function getDimensionValue(value, fallback) {
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string' && value.trim()) return value;
    return `${fallback}px`;
}

function getNumericDimension(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getOptionalDimensionValue(value) {
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string' && value.trim()) return value;
    return null;
}

function getWorkspaceImageSource(workspace, assetType) {
    if (assetType === 'cover') {
        return typeof workspace?.sidebarCoverPhotoUrl === 'string'
            ? workspace.sidebarCoverPhotoUrl.trim()
            : '';
    }

    const iconPhotoUrl = typeof workspace?.iconPhotoUrl === 'string'
        ? workspace.iconPhotoUrl.trim()
        : '';

    if (iconPhotoUrl) {
        return iconPhotoUrl;
    }

    return typeof workspace?.coverPhotoUrl === 'string'
        ? workspace.coverPhotoUrl.trim()
        : '';
}

/**
 * WorkspaceCover
 *
 * Renders a workspace cover image when available, or workspace initials fallback.
 *
 * @param {object} workspace - Workspace model containing title and workspace image fields.
 * @param {number} size - Render size in pixels.
 * @param {string|number} width - Optional rendered width.
 * @param {string|number} height - Optional rendered height.
 * @param {string|number} maxHeight - Optional max height constraint.
 * @param {string} assetType - Image source selector: `icon` or `cover`.
 * @param {number} borderWidth - Container border width in pixels.
 * @param {string|number} borderRadius - Corner radius for square shapes.
 * @param {function} onClick - Optional click handler for the cover container.
 * @param {string} shape - Display shape from PhotoShape enum.
 *
 * @returns {ReactNode} The rendered workspace cover.
 */
const WorkspaceCover = React.memo(({
    workspace,
    size = 40,
    width = null,
    height = null,
    maxHeight = null,
    assetType = 'icon',
    borderWidth = 2,
    borderRadius = '7px',
    onClick,
    shape = PhotoShape.CIRCLE,
}) => {

    // ----- STATE ----- //

    // Resolved Image URL State
    const [resolvedImageUrl, setResolvedImageUrl] = useState(null);

    // Image Loaded State
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    // Image Error State
    const [hasImageError, setHasImageError] = useState(false);

    // ----- UTILITIES ----- //

    // Normalized persisted workspace image path.
    const imageSourceUrl = getWorkspaceImageSource(workspace, assetType);

    // Effective container/image radius based on selected shape.
    const radius = shape === PhotoShape.CIRCLE ? '50%' : borderRadius;

    // Resolved dimensions support square and banner-style renders.
    const resolvedWidth = getDimensionValue(width, size);
    const resolvedHeight = getDimensionValue(height, size);
    const resolvedMaxHeight = getOptionalDimensionValue(maxHeight);

    // Keep fallback initials sized from the smallest explicit numeric dimension.
    const numericWidth = getNumericDimension(width);
    const numericHeight = getNumericDimension(height);
    const explicitDimensions = [numericWidth, numericHeight].filter((value) => value !== null);
    const initialSizeBasis = explicitDimensions.length > 0
        ? Math.min(...explicitDimensions)
        : size;

    // Workspace initials fallback for missing covers.
    const initials = getWorkspaceInitials(workspace?.title);
    const altText = assetType === 'cover'
        ? (workspace?.title || 'Workspace cover')
        : (workspace?.title || 'Workspace icon');

    // ----- EFFECTS ----- //

    // Resolves storage paths to downloadable URLs when needed.
    useEffect(() => {
        let cancelled = false;

        setIsImageLoaded(false);
        setHasImageError(false);

        if (!imageSourceUrl) {
            setResolvedImageUrl(null);
            return undefined;
        }

        const raw = imageSourceUrl;

        if (isDirectRenderableImageUrl(raw)) {
            setResolvedImageUrl(raw);
            return undefined;
        }

        (async () => {
            try {
                const url = await resolveImageUrl({
                    workspaceKey: workspace?.key || workspace?.id || null,
                    url: raw,
                });
                if (!cancelled) setResolvedImageUrl(url || null);
            } catch {
                if (!cancelled) setResolvedImageUrl(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [imageSourceUrl, workspace?.key, workspace?.id]);

    // ----- RENDER ----- //

    if (!workspace) return null;

    const canRenderImage = Boolean(imageSourceUrl && resolvedImageUrl && !hasImageError);
    const showPlaceholder = Boolean(imageSourceUrl) && !hasImageError && !isImageLoaded;
    const showInitials = !imageSourceUrl || hasImageError;

    return (
        <div
            className="workspace-cover-container"
            style={{
                borderRadius: radius,
                borderWidth: `${borderWidth}px`,
                height: 'auto', //resolvedHeight,
                maxHeight: resolvedMaxHeight || undefined,
                width: resolvedWidth,
            }}
            onClick={onClick}
        >

            {/* SHIMMER */}
            {showPlaceholder && (
                <div
                    className="workspace-cover-shimmer"
                    style={{ borderRadius: radius }}
                />
            )}

            {/* IMAGE */}
            {canRenderImage && (
                <img
                    className="workspace-cover-image"
                    style={{
                        borderRadius: radius,
                        opacity: isImageLoaded ? 1 : 0,
                    }}
                    alt={altText}
                    src={resolvedImageUrl}
                    onLoad={() => setIsImageLoaded(true)}
                    onError={() => {
                        setHasImageError(true);
                        setIsImageLoaded(false);
                    }}
                />
            )}

            {/* INITIALS */}
            {showInitials && (
                <div
                    className="workspace-cover-initial"
                    style={{
                        '--dot-font-size': `${initialSizeBasis * 0.5}px`,
                        '--dot-height': resolvedHeight,
                        '--dot-size': `${size}px`,
                        '--dot-width': resolvedWidth,
                        borderRadius: radius,
                    }}
                >
                    {initials}
                </div>
            )}
        </div>
    );
});

// Dev Tools
WorkspaceCover.displayName = 'WorkspaceCover';

export default WorkspaceCover;